import { buildEntityColumns, entityModel } from '@/composables/entityColumns';

/**
 * Each record type's quick search fields (SAU-467), read from models.json, where
 * `make:ts-interfaces` writes them from the backend model. The list search box, Cmd+K
 * and the search page all read this one list, so they cannot disagree.
 *
 * - `defaults`: the fields searched until a person changes them.
 * - `offered`: every field the tag row shows, which is every field typed text can be
 *   matched in. The server refuses any other field.
 */

export type QuickSearchMode = 'all' | 'any';

export const QUICK_SEARCH_MODES: readonly QuickSearchMode[] = ['all', 'any'];

export interface QuickSearchTag {
    /** The field's path, as the backend names it, such as `email` or `location_zone.display_string`. */
    key: string;
    /** What the tag reads: the column's header where the list has a column for it. */
    label: string;
    /** `number` fields match only the exact whole number. */
    kind: 'text' | 'number';
}

export interface QuickSearchSelection {
    fields: string[];
    mode: QuickSearchMode;
}

interface QuickSearchEntry {
    defaults: string[];
    offered: { key: string; label: string; kind: 'text' | 'number' }[];
}

function quickSearchEntry(entityId: string): QuickSearchEntry | null {
    const entry = (entityModel(entityId) as { _quickSearch?: QuickSearchEntry } | undefined)?._quickSearch;
    return entry && Array.isArray(entry.defaults) && Array.isArray(entry.offered) && entry.defaults.length > 0 ? entry : null;
}

/** Whether the record type has quick search, and so a search box. */
export function hasQuickSearch(entityId: string): boolean {
    return quickSearchEntry(entityId) !== null;
}

/** The fields searched until a person changes them. */
export function defaultQuickSearchFields(entityId: string): string[] {
    return [...(quickSearchEntry(entityId)?.defaults ?? [])];
}

/** The selection a record type starts with: its default fields and Matches all. */
export function defaultQuickSearchSelection(entityId: string): QuickSearchSelection {
    return { fields: defaultQuickSearchFields(entityId), mode: 'all' };
}

/**
 * Every tag the row shows, sorted A to Z by label. A tag reads like the column the list
 * shows for that field, so "Last name" in the row matches "Last name" in the table.
 */
export function quickSearchTags(entityId: string): QuickSearchTag[] {
    const entry = quickSearchEntry(entityId);
    if (!entry) {
        return [];
    }
    const headerByKey = new Map(buildEntityColumns(entityId).map((column) => [column.key, column.header]));
    return entry.offered
        .map((field) => ({ key: field.key, label: headerByKey.get(field.key) ?? field.label, kind: field.kind }))
        .sort((first, second) => first.label.localeCompare(second.label, undefined, { sensitivity: 'base' }));
}

/** The tag for one field key, or undefined when the record type does not offer it. */
export function quickSearchTag(entityId: string, key: string): QuickSearchTag | undefined {
    return quickSearchTags(entityId).find((tag) => tag.key === key);
}

/**
 * A stored or sent selection, cleaned for the record type as it is now: a field it no
 * longer offers is dropped, a repeated field is dropped, and when no field is left the
 * defaults apply. A mode that is not "all" or "any" becomes "all".
 */
export function sanitizeQuickSearchSelection(entityId: string, raw: unknown): QuickSearchSelection {
    const offered = new Set(quickSearchEntry(entityId)?.offered.map((field) => field.key) ?? []);
    const candidate = (typeof raw === 'object' && raw !== null ? raw : {}) as { fields?: unknown; mode?: unknown };
    const fields = Array.isArray(candidate.fields)
        ? [...new Set(candidate.fields.filter((field): field is string => typeof field === 'string' && offered.has(field)))]
        : [];
    const mode = QUICK_SEARCH_MODES.includes(candidate.mode as QuickSearchMode) ? (candidate.mode as QuickSearchMode) : 'all';
    return { fields: fields.length > 0 ? fields : defaultQuickSearchFields(entityId), mode };
}
